import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Meteor } from 'meteor/meteor';
import { withTracker } from 'meteor/react-meteor-data';
import { filter, find, get, isEmpty, isEqual, isNil, map } from 'lodash';
import { Table } from 'react-bootstrap';

import * as templateOps from '../../../api/AnnotationTemplates/annotation-template-operations';
import * as annotationOps from '../../../api/Annotations/annotation-operations';
import Annotations from '../../../api/Annotations/Annotations';

import Loading from '../../components/Loading';
import TableContent from '../../components/TableContent';
import TablePagination from '../../components/TablePagination';
import ViewAnnotation from '../../components/ViewAnnotation';

import { GridContainer, Grid, GridLeftChild, GridRightChild } from './styles';

const HEADER_HEIGHT = 74; // When it is all in the same line
const PAGINATION_HEIGHT = 46;
const CELL_HEIGHT = 49;

class AnnotationsAtlas extends Component {
  annotationDiv = React.createRef();

  tableElement = React.createRef();

  state = {
    currentPage: 1,
    itemsPerPage: 1,
    selectedAnnotation: {}
  };

  componentDidMount() {
    window.addEventListener('resize', this.updateDimensions);
    this.updateDimensions();
  }

  componentDidUpdate(prevProps) {
    const { annotations, loading, video } = this.props;
    const { loading: prevLoading, video: prevVideo } = prevProps;

    if (!isEqual(loading, prevLoading) && !isEmpty(annotations))
      this.selectAnnotation(annotationOps.getId(annotations[0]));

    if (!isEqual(video, prevVideo)) this.selectAnnotation();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDimensions);
  }

  changePage = pageNumber => this.setState({ currentPage: pageNumber });

  changeStateProperty = (property, value) => this.setState({ [property]: value });

  selectAnnotation = annotationId => {
    const { annotations } = this.props;
    const selectedAnnotation = find(annotations, ['_id', annotationId]) || {};
    this.setState({ selectedAnnotation });
    if (!isNil(this.annotationDiv.current)) this.annotationDiv.current.scrollTop = 0;
  };

  updateDimensions = () => {
    const height =
      get(this, 'tableElement.current.offsetHeight', 500) - (PAGINATION_HEIGHT + HEADER_HEIGHT);

    let itemsPerPage = Math.floor(height / CELL_HEIGHT);
    if (itemsPerPage < 1) itemsPerPage = 1;

    this.setState({ itemsPerPage });
  };

  render() {
    const {
      annotations,
      gridBackgroundColor,
      isPreview,
      loading,
      totalCount,
      isPreviewMapEditor
    } = this.props;
    const { currentPage, itemsPerPage, selectedAnnotation } = this.state;

    const skip = currentPage * itemsPerPage - itemsPerPage;
    const annotationsForTable = map(annotations, annotation => ({
      _id: annotationOps.getId(annotation),
      name: templateOps.getName(annotationOps.getTemplate(annotation)),
      icon: templateOps.getIcon(annotationOps.getTemplate(annotation))
    }));
    const annotationsToShow = filter(
      annotationsForTable,
      (annotation, index) => index >= skip && index < skip + itemsPerPage
    );

    const listProps = {
      clickAction: 'select',
      datesColumns: false,
      deleteButton: false,
      iconColumn: true,
      iconTooltip: true,
      itemIdentifier: 'id',
      items: annotationsToShow,
      loading: false,
      selectedRow: annotationOps.getId(selectedAnnotation),
      stretchView: isPreview,
      handleSelectRow: this.selectAnnotation
    };

    if (loading) return <Loading />;
    return (
      <GridContainer backgroundcolor={gridBackgroundColor} fullScreen>
        <Grid>
          <GridLeftChild xs={12} md={isPreview ? 4 : 2}>
            <div ref={this.tableElement} className="h-100">
              <Table responsive hover size="lg">
                <TableContent
                  isTableEmpty={isEmpty(annotations)}
                  itemName="Annotations"
                  numberOfColumns={2}
                  {...listProps}
                />
              </Table>
              <TablePagination
                currentPage={currentPage}
                isPaginationOn
                itemsPerPage={itemsPerPage}
                stretchView
                totalCount={totalCount}
                changePage={this.changePage}
              />
            </div>
          </GridLeftChild>
          <GridRightChild xs={12} md={isPreview ? 8 : 10}>
            <div
              ref={this.annotationDiv}
              style={{
                overflowY: 'auto',
                height: '95%',
                overflowX: 'hidden'
              }}
            >
              <ViewAnnotation
                annotation={selectedAnnotation}
                changeVideoContainerStateProperty={this.changeStateProperty}
                isPublic
                isPreviewMapEditor={isPreviewMapEditor}
              />
            </div>
          </GridRightChild>
        </Grid>
      </GridContainer>
    );
  }
}

AnnotationsAtlas.defaultProps = {
  annotations: [],
  gridBackgroundColor: '#98c1de',
  isPreview: false,
  totalCount: 0,
  video: '',
  isPreviewMapEditor: false
};

AnnotationsAtlas.propTypes = {
  annotations: PropTypes.array,
  gridBackgroundColor: PropTypes.string,
  isPreview: PropTypes.bool,
  loading: PropTypes.bool.isRequired,
  totalCount: PropTypes.number,
  video: PropTypes.string,
  isPreviewMapEditor: PropTypes.bool
};

export default withTracker(({ video }) => {
  let annotationSub = {};
  let annotations = [];
  let totalCount = 0;
  if (!isEmpty(video)) {
    annotationSub = Meteor.isClient && Meteor.subscribe('annotations.video', video);
    annotations = Annotations.find({}, { sort: { videoTime: 1 } }).fetch();
    totalCount = Annotations.find({}, { sort: { videoTime: 1 } }).count();
  }
  return {
    annotations,
    loading: !isEmpty(video) && !annotationSub.ready(),
    totalCount
  };
})(AnnotationsAtlas);
